import { HttpErrorResponse } from '@angular/common/http';
import { MonoTypeOperatorFunction, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

/**
 * Drop-in replacement for a bare `retry(n)` on an HTTP effect.
 *
 * A plain `retry(n)` retries on ANY error emitted by the source - including
 * real, non-transient failures (400 validation errors, 404s, 500s, and 401s
 * that the auth interceptor already handled). That caused every genuine
 * failure to be replayed 2-3x against the backend, showing up as duplicated
 * red requests in the Network tab.
 *
 * This only retries errors that look transient/network-level (no connection,
 * request timeout, or a gateway/upstream error), with a short backoff, and
 * never retries an error the interceptor has already surfaced to the user.
 */
export function retryTransientHttpErrors<T>(maxRetries = 2, delayMs = 500): MonoTypeOperatorFunction<T> {
  const RETRYABLE_STATUSES = [0, 408, 502, 503, 504];
  const ALREADY_HANDLED_MESSAGES = ['401_ALREADY_HANDLED', '401_UNAUTHORIZED_HANDLED'];

  return retryWhen<T>(errors =>
    errors.pipe(
      mergeMap((error, index) => {
        const attempt = index + 1;
        const isAlreadyHandled = ALREADY_HANDLED_MESSAGES.includes(error?.message);
        const isTransient = error instanceof HttpErrorResponse && RETRYABLE_STATUSES.includes(error.status);

        if (isAlreadyHandled || !isTransient || attempt > maxRetries) {
          return throwError(() => error);
        }

        return timer(delayMs * attempt);
      })
    )
  );
}
